import classnames from "classnames";
import React, { useEffect, useRef } from "react";
import PrimaryButton from "web/react/components/buttons/primary-button/primary-button";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import { gettext } from "web/script/modules/django-i18n";
import { AllSelectedFilterInfo, StandardFilterProps } from "web/types/feed-filters";
import { UniversalFiltersData } from "web/types/universal-filters";
import BreadcrumbPills from "./feeds-filter-breadcrumb-pills/feeds-filter-breadcrumb-pills";
import FilterPagination from "./feeds-filters-pagination/filter-pagination";
import { PaginatedItem, PaginationProps } from "./feeds-filters-pagination/pagination-state";
import styles from "./feeds-filters.css";

interface FeedFiltersProps extends PaginationProps, StandardFilterProps {
    applyState(): void;
    canApply: boolean;
    displayedFilters: UniversalFiltersData;
    isLoading: boolean;
    lazyFacetFetch(optionGroupId: string): void;
    lazyFacetIsLoading: boolean;
    onClose?: (item: PaginatedItem) => void;
    selectedFilterInfo: AllSelectedFilterInfo;
    sidebar?: boolean;
}

// Shows roughly half a pill
const PILL_PEEK_HEIGHT = 35;

function FeedFilters({
    applyState,
    canApply,
    displayedFilters,
    isLoading,
    lazyFacetFetch,
    lazyFacetIsLoading,
    onClick,
    onClose,
    onPageChange,
    paginationState,
    pendingFilterInfo,
    selectedFilterInfo,
    sidebar,
}: FeedFiltersProps): JSX.Element {
    let scrollAreaRef = useRef<HTMLDivElement>(null);
    let pillContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!scrollAreaRef.current) {
            return;
        }

        let top = 0;
        // On any page that's not the main page, we want to show a hint of
        //  breadcrumbs but not all the breadcrumbs.
        if (paginationState.hasActivePage() && pillContainerRef.current) {
            let pillsHeight = pillContainerRef.current.clientHeight;
            if (pillsHeight > PILL_PEEK_HEIGHT) {
                top = pillsHeight - PILL_PEEK_HEIGHT;
            }
        }
        scrollAreaRef.current.scrollTop = top;
    }, [paginationState]);

    return (
        <div
            className={classnames(styles.container, {
                [styles.sidebar]: sidebar,
                [styles.overlay]: !sidebar,
                [styles.loading]: isLoading,
            })}
        >
            <div className={styles.content} ref={scrollAreaRef}>
                <h3 className={styles.title}>{gettext("general.filters")}</h3>
                <div ref={pillContainerRef}>
                    <BreadcrumbPills
                        className={styles.breadcrumbs}
                        onClick={onClick}
                        selectedFilterInfo={selectedFilterInfo}
                    />
                </div>
                <FilterPagination
                    allSelectedFilterInfo={selectedFilterInfo}
                    lazyFacetFetch={lazyFacetFetch}
                    lazyFacetIsLoading={lazyFacetIsLoading}
                    onClick={onClick}
                    sections={displayedFilters.filter_section_list}
                    onClose={onClose}
                    sidebar={sidebar}
                    {...{ onPageChange, paginationState, pendingFilterInfo }}
                />
            </div>
            <div className={styles.applyButton}>
                <PrimaryButton onClick={applyState} fullWidth={true} disabled={!canApply}>
                    {"Apply Filters"}
                    {/* // TODO: Just realised we don't have a differences count */}
                </PrimaryButton>
            </div>
            {isLoading && <LoadingSpinner />}
        </div>
    );
}

export default FeedFilters;
